import React, { useEffect, useState } from "react";
import { DownloadSimple, X, ShareNetwork, Plus } from "@phosphor-icons/react";

const DISMISS_KEY = "wahdat_install_dismissed_v1";

function isIOS() {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent) && !window.MSStream;
}
function isStandalone() {
  return window.matchMedia?.("(display-mode: standalone)").matches
    || window.navigator.standalone === true;
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [showIOSHint, setShowIOSHint] = useState(false);
  const [installed, setInstalled] = useState(isStandalone());
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");

  useEffect(() => {
    function onBeforeInstall(e) {
      e.preventDefault();
      setDeferred(e);
    }
    function onInstalled() {
      setInstalled(true);
      setDeferred(null);
    }
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  async function handleInstall() {
    if (deferred) {
      deferred.prompt();
      try {
        await deferred.userChoice;
      } catch { /* ignore */ }
      setDeferred(null);
    } else if (isIOS() && !isStandalone()) {
      setShowIOSHint(true);
    }
  }

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  }

  // Never show once installed
  if (installed) return null;

  // Show a persistent floating bar until dismissed OR installed.
  // iOS gets a hint modal (no beforeinstallprompt on Safari).
  const canInstallInline = !!deferred;
  const canShowIOS = isIOS() && !isStandalone();
  if (!canInstallInline && !canShowIOS) return null;
  if (dismissed && !showIOSHint) return null;

  return (
    <>
      {!dismissed && (
        <div
          className="fixed bottom-20 lg:bottom-6 left-3 right-3 lg:left-auto lg:right-6 lg:w-80 z-40 bg-[#0a0a0a] border border-neutral-800 rounded-lg shadow-xl p-4"
          data-testid="install-prompt"
        >
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-md bg-[#D90429] flex items-center justify-center shrink-0">
              <DownloadSimple size={22} weight="bold" color="#fff" />
            </div>
            <div className="flex-1">
              <div className="font-display font-bold text-base uppercase tracking-tight leading-tight">Install Wahdat FC</div>
              <div className="text-xs text-neutral-400 mt-1">Add it to your home screen for full-screen, offline-friendly access.</div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={handleInstall}
                  data-testid="install-app-btn"
                  className="h-10 px-4 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white font-display font-bold uppercase tracking-wider text-xs"
                >
                  Install
                </button>
                <button
                  onClick={dismiss}
                  data-testid="install-dismiss-btn"
                  className="h-10 px-3 rounded-md border border-neutral-800 hover:border-neutral-700 text-xs font-bold uppercase tracking-wider text-neutral-400"
                >
                  Not now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showIOSHint && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4" onClick={() => setShowIOSHint(false)}>
          <div className="bg-[#121212] border border-neutral-800 rounded-lg p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()} data-testid="ios-install-modal">
            <div className="flex justify-between items-start">
              <div>
                <div className="overline">Install on iPhone / iPad</div>
                <div className="font-display font-black text-xl uppercase mt-1">3 quick steps</div>
              </div>
              <button onClick={() => setShowIOSHint(false)} className="p-2 rounded-md hover:bg-neutral-900" data-testid="ios-modal-close">
                <X size={20} weight="bold" />
              </button>
            </div>
            <ol className="space-y-4 mt-4 text-sm">
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-[#D90429] text-white flex items-center justify-center font-bold shrink-0">1</span>
                <div>Open this page in <b>Safari</b> (not Chrome).</div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-[#D90429] text-white flex items-center justify-center font-bold shrink-0">2</span>
                <div className="flex items-center gap-2">
                  Tap the <ShareNetwork size={18} weight="bold" color="#3b82f6" className="inline" /> <b>Share</b> button (bottom of Safari).
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-[#D90429] text-white flex items-center justify-center font-bold shrink-0">3</span>
                <div className="flex items-center gap-2">
                  Tap <Plus size={16} weight="bold" className="inline" /> <b>Add to Home Screen</b>, then tap <b>Add</b>.
                </div>
              </li>
            </ol>
            <div className="mt-5 text-xs text-neutral-500">The app will appear on your home screen just like a native app.</div>
          </div>
        </div>
      )}
    </>
  );
}
